import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import { SECTION_CATALOG, type SectionId } from '../api/generated/section-catalog';
import { GRANTED_ROLES, ROLE_LABELS, type GrantedRole } from '../../../api/src/auth/access-role';
import {
  PERMISSIONS,
  permissionDecision,
  type PermissionOverrides,
} from '../../../api/src/auth/permissions';
import { api, ApiError } from '../api/client';
import type { AccessGrant } from '../api/types';

export function PermissionsDialog({
  grant,
  onClose,
  onSaved,
}: {
  grant: AccessGrant;
  onClose(): void;
  onSaved(): Promise<void>;
}) {
  const [sectionId, setSectionId] = useState<SectionId>('positions');
  const section = SECTION_CATALOG.find((item) => item.id === sectionId)!;
  const [role, setRole] = useState<GrantedRole>(grant.role);
  const [overrides, setOverrides] = useState<PermissionOverrides>({ ...grant.permissionOverrides });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [current, setCurrent] = useState(grant);
  const subject = {
    role,
    access: { grantId: grant.id, revision: current.permissionRevision, overrides },
  };
  async function reload() {
    setSaving(true);
    try {
      const fresh = (await api.access()).allowedUsers.find((row) => row.id === grant.id);
      if (!fresh) throw new Error('Доступ пользователя отозван. Закройте окно и обновите список.');
      setCurrent(fresh);
      setRole(fresh.role);
      setOverrides({ ...fresh.permissionOverrides });
      setConflict(false);
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось обновить права');
    } finally {
      setSaving(false);
    }
  }
  async function save() {
    if (saving || conflict) return;
    setSaving(true);
    setError('');
    try {
      await api.updatePermissions(grant.id, {
        role,
        overrides,
        revision: current.permissionRevision,
      });
      await onSaved();
    } catch (caught) {
      setConflict(caught instanceof ApiError && caught.status === 409);
      setError(caught instanceof Error ? caught.message : 'Не удалось сохранить права');
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="permissions-title"
    >
      <DialogTitle id="permissions-title">Права пользователя · {grant.email}</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Typography>
            Один набор прав действует во всех доступных проектах. Персональное исключение
            сохраняется при смене базовой роли.
          </Typography>
          {error && (
            <Alert
              severity="error"
              action={
                conflict ? (
                  <Button disabled={saving} onClick={() => void reload()}>
                    Обновить права
                  </Button>
                ) : undefined
              }
            >
              {error}
            </Alert>
          )}
          <TextField
            select
            label="Базовая роль"
            value={role}
            disabled={saving || conflict}
            onChange={(e) => setRole(e.target.value as GrantedRole)}
          >
            {GRANTED_ROLES.map((value) => (
              <MenuItem key={value} value={value}>
                {ROLE_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>
          <Typography color="text.secondary" variant="body2">
            «По роли» убирает исключение. Без просмотра раздела остальные разрешения не действуют.
            Здесь показаны только применимые операции.
          </Typography>
          <TextField
            select
            label="Раздел"
            value={sectionId}
            onChange={(event) => setSectionId(event.target.value as SectionId)}
          >
            {SECTION_CATALOG.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Персональных исключений: {Object.keys(overrides).length}. Сохраняются изменения всех
            разделов.
          </Typography>
          <div key={section.id}>
            <Typography variant="h3" mb={1}>
              {section.label}
            </Typography>
            <TableContainer>
              <Table
                size="small"
                aria-label={`Права: ${section.label}`}
                sx={{
                  '& thead': { display: { xs: 'none', sm: 'table-header-group' } },
                  '& tbody': { display: { xs: 'block', sm: 'table-row-group' } },
                  '& tbody tr': {
                    display: { xs: 'grid', sm: 'table-row' },
                    gridTemplateColumns: '1fr',
                    borderBottom: { xs: '1px solid', sm: 0 },
                    borderColor: 'divider',
                    py: { xs: 1, sm: 0 },
                  },
                  '& tbody td, & tbody th': {
                    display: { xs: 'block', sm: 'table-cell' },
                    borderBottom: { xs: 0, sm: '1px solid' },
                    borderColor: 'divider',
                    px: { xs: 0, sm: 2 },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Операция</TableCell>
                    <TableCell>Настройка</TableCell>
                    <TableCell>Итог и основание</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PERMISSIONS.filter((p) => p.section === section.id).map((p) => {
                    const decision = permissionDecision(subject, p.key);
                    const value =
                      overrides[p.key] === undefined
                        ? 'inherit'
                        : overrides[p.key]
                          ? 'allow'
                          : 'deny';
                    return (
                      <TableRow key={p.key}>
                        <TableCell component="th" scope="row">
                          {p.label}
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            slotProps={{
                              select: {
                                inputProps: { 'aria-label': `${section.label}: ${p.label}` },
                              },
                            }}
                            fullWidth
                            value={value}
                            sx={{ minWidth: 160 }}
                            disabled={saving || conflict}
                            onChange={(e) =>
                              setOverrides((previous) => {
                                const next = { ...previous };
                                if (e.target.value === 'inherit') delete next[p.key];
                                else next[p.key] = e.target.value === 'allow';
                                return next;
                              })
                            }
                          >
                            <MenuItem value="inherit">По роли</MenuItem>
                            <MenuItem value="allow">Разрешено</MenuItem>
                            <MenuItem value="deny">Запрещено</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>
                            {decision.allowed ? 'Разрешено' : 'Запрещено'}
                          </Typography>
                          <Typography fontSize={12} color="text.secondary">
                            {decision.reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Отмена
        </Button>
        <Button disabled={saving || conflict} variant="contained" onClick={() => void save()}>
          {saving ? 'Сохраняем…' : 'Сохранить права'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
